import ReceiptDetail from "../components/ReceiptDetail";
import MedianReceiptModal from "../components/MedianReceiptModal";
import React, { useEffect, useState } from "react";
import { IconCard, IconCart, IconCash, IconChart, IconReceipt, IconUsers, IconClock, IconWarning } from "../components/ui/icons";
import QuickPeriodBar from "../components/QuickPeriodBar";
import KpiCompareCard from "../components/KpiCompareCard";
import RevenueChart from "../components/RevenueChart";
import SalesByDayModal from "../components/SalesByDayModal";
import TopItemsList from "../components/charts/TopItemsList";
import PaymentsDonut from "../components/PaymentsDonut";
import CategoryChart from "../components/charts/CategoryChart";
import GroupShareChart from "../components/charts/GroupShareChart";
import Top5ItemsChart from "../components/charts/Top5ItemsChart";
import { formatNumber } from "../components/charts/chartTheme";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import EmployeeSplitBars from "../components/EmployeeSplitBars";
import ExpenseDetail from "../components/ExpenseDetail";
import { ChartSkeleton, ListSkeleton, Skeleton } from "../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../components/ui/StateBlock";
import Modal from "../components/ui/Modal";
import RecordListModal from "../components/RecordListModal";
import LoadingOverlay from "../components/ui/LoadingOverlay";
import MultiSelectDropdown from "../components/ui/MultiSelectDropdown";
import { useTheme } from "../context/ThemeContext";
import { useApiData } from "../hooks/useApiData";
import { percentChange, usePeriodStats } from "../hooks/usePeriodStats";
import { useWarehouses } from "../hooks/useWarehouses";
import { useActiveCompany } from "../hooks/useActiveCompany";
import OccupancyMeter from "../components/OccupancyMeter";
import { unwrapArray } from "../utils/api";
import { formatDateRange, getComparisonRange, getQuickPeriodRange, todayInputValue } from "../utils/date";
import CompareToggle from "../components/CompareToggle";

export default function Dashboard() {
  const { darkMode } = useTheme();
  const [period, setPeriod] = useState("danas");
  const [startDate, setStartDate] = useState(todayInputValue);
  const [endDate, setEndDate] = useState(todayInputValue);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  // "previous" = previous period, "year" = the same dates a year earlier (remembered per browser)
  const [compareMode, setCompareMode] = useState(() => {
    try {
      return localStorage.getItem("dashboard-compare") === "year" ? "year" : "previous";
    } catch {
      return "previous";
    }
  });
  const changeCompareMode = (mode) => {
    setCompareMode(mode);
    try {
      localStorage.setItem("dashboard-compare", mode);
    } catch {
      /* storage unavailable */
    }
  };
  const [showRevenueDetail, setShowRevenueDetail] = useState(false);
  const [showSalesAnalysis, setShowSalesAnalysis] = useState(false);
  const [showReceipts, setShowReceipts] = useState(false);
  const [showExpenses, setShowExpenses] = useState(false);
  const [showMedian, setShowMedian] = useState(false);

  const { warehouses } = useWarehouses();
  const warehouse = selectedWarehouses.join(",");
  const whParam = warehouse ? `&warehouse=${warehouse}` : "";
  const [refreshTick, setRefreshTick] = useState(0);

  // Wait for the default warehouse to load and apply BEFORE anything else
  // starts fetching - without this, the first fetch used an empty
  // warehouse, then again as soon as the settings arrived (a visible "loads then
  // reloads" effect).
  // Load order: active company (name, info, logo), then settings, then the rest of the dashboard.
  const company = useActiveCompany();
  const [settingsApplied, setSettingsApplied] = useState(false);
  const { data: appSettingsRaw, error: appSettingsError } = useApiData(company.ready ? "/settings_get.php" : null);
  useEffect(() => {
    // open the gate even when the settings do not arrive (server error) - otherwise it stays on the spinner forever
    if (company.ready && (appSettingsRaw !== null || appSettingsError)) {
      if (appSettingsRaw?.success && appSettingsRaw.default_magacin) {
        setSelectedWarehouses(appSettingsRaw.default_magacin.split(",").filter(Boolean));
      }
      setSettingsApplied(true);
    }
  }, [company.ready, appSettingsRaw, appSettingsError]);

  const handlePeriodChange = (type) => {
    setPeriod(type);
    const range = getQuickPeriodRange(type);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setRefreshTick((t) => t + 1); // a click on an already active period still refreshes the data
  };

  const handleCustomApply = (customStartDate, customEndDate) => {
    setPeriod("prilagodi");
    setStartDate(customStartDate);
    setEndDate(customEndDate);
    setRefreshTick((t) => t + 1);
  };

  const { startDate: prevStartDate, endDate: prevEndDate } = getComparisonRange(startDate, endDate, period, compareMode);

  const current = usePeriodStats(startDate, endDate, warehouse, refreshTick, settingsApplied);
  const previous = usePeriodStats(prevStartDate, prevEndDate, warehouse, refreshTick, settingsApplied);

  const { data: overviewExtraRaw, loading: loadingOverviewExtra, error: overviewExtraError } = useApiData(
    settingsApplied
      ? `/overview_extra.php?od=${startDate}&do=${endDate}&prevOd=${prevStartDate}&prevDo=${prevEndDate}${whParam}`
      : null,
    refreshTick
  );
  const expense = overviewExtraRaw?.success ? parseFloat(overviewExtraRaw.rashod) || 0 : 0;
  const expensePrev = overviewExtraRaw?.success ? parseFloat(overviewExtraRaw.rashod_prethodni) || 0 : 0;
  const payments = unwrapArray(overviewExtraRaw?.placanja);

  const { data: shiftDashboardRaw, loading: loadingShiftDashboard, error: shiftError } = useApiData(settingsApplied ? "/shift_dashboard.php" : null, refreshTick);
  const shiftPayments = unwrapArray(shiftDashboardRaw?.placanja);
  const shiftCash = shiftPayments.find((p) => /gotovin/i.test(p.paytypename))?.sumpayvalue || 0;
  const shiftCards = shiftPayments.find((p) => /kartic/i.test(p.paytypename))?.sumpayvalue || 0;
  const shiftExpense = shiftDashboardRaw?.success ? parseFloat(shiftDashboardRaw.rashod) || 0 : 0;
  const activeWaiters = shiftDashboardRaw?.success ? shiftDashboardRaw.konobari || [] : [];
  const bestTable = shiftDashboardRaw?.success ? shiftDashboardRaw.najbolji_sto : null;
  const bestTableValue = shiftDashboardRaw?.success ? parseFloat(shiftDashboardRaw.najbolji_sto_vrednost) || 0 : 0;

  const { data: tablesRaw } = useApiData(settingsApplied ? "/tables.php" : null, refreshTick);
  const tables = unwrapArray(tablesRaw, "tables");
  const currentlyOnTables = tables.reduce((sum, t) => sum + (parseFloat(t.Vrednost) || 0), 0);
  const occupancy = tables.length > 0 ? (tables.filter((t) => Number(t.Zauzet) === 1).length / tables.length) * 100 : 0;

  const { data: employeeRevenueRaw, loading: loadingEmployeeRevenue, error: employeeError } = useApiData(
    settingsApplied ? `/employee_summary.php?od=${startDate}&do=${endDate}${whParam}` : null,
    refreshTick
  );
  const employeeRevenue = [...unwrapArray(employeeRevenueRaw)].sort(
    (a, b) => (parseFloat(b.prihod) || 0) - (parseFloat(a.prihod) || 0)
  );

  const compareProps =
    compareMode === "year"
      ? { compareLabel: "prošla godina", compareLabelLong: "u odnosu na prošlu godinu" }
      : {};

  const revenueDiff = current.promet - previous.promet;
  const handleRetry = () => setRefreshTick((t) => t + 1);
  const kpiPending = current.loading && current.hourlyRows.length === 0;

  return (
    <div className="flex flex-col gap-4">

      {/* Filters: the period bar on its own row, below it Warehouse + Comparison side by side (Warehouse first, so it keeps its position) (the layout does not jump with the custom date pickers) */}
      <div className="flex flex-col gap-3">
        <QuickPeriodBar
          period={period}
          onPeriodChange={handlePeriodChange}
          startDate={startDate}
          endDate={endDate}
          onCustomApply={handleCustomApply}
        />
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <MultiSelectDropdown
            label="Magacin"
            options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            selected={selectedWarehouses}
            onChange={setSelectedWarehouses}
          />
          <CompareToggle mode={compareMode} onChange={changeCompareMode} startDate={prevStartDate} endDate={prevEndDate} />
        </div>
      </div>

      {!settingsApplied ? (
        <DashboardSkeleton />
      ) : (
        <>
          {current.error && (
            <div
              role="alert"
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <span className="flex items-center gap-2">
                <IconWarning aria-hidden="true" className="shrink-0 text-lg text-danger dark:text-danger-light" />
                Podaci o prometu nisu učitani. Ostali delovi stranice mogu da se prikazuju normalno.
              </span>
              <Button variant="secondary" size="sm" onClick={handleRetry}>
                Pokušaj ponovo
              </Button>
            </div>
          )}

          {/* KPI strip: one panel with cells separated by lines, instead of four large cards */}
          <LoadingOverlay loading={current.loading || previous.loading}>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 dark:border-slate-800 dark:bg-slate-800 lg:grid-cols-4">
              <KpiCompareCard
                label="Promet"
                value={formatNumber(current.promet)}
                unit="RSD"
                changePct={percentChange(current.promet, previous.promet)}
                icon={IconCash}
                pending={kpiPending}
                {...compareProps}
                onClick={() => setShowReceipts(true)}
              />
              <KpiCompareCard
                label="Broj računa"
                value={formatNumber(current.brojRacuna)}
                changePct={percentChange(current.brojRacuna, previous.brojRacuna)}
                icon={IconReceipt}
                pending={kpiPending}
                {...compareProps}
              />
              <KpiCompareCard
                label="Prosečan račun"
                value={formatNumber(current.prosecanRacun)}
                unit="RSD"
                changePct={percentChange(current.prosecanRacun, previous.prosecanRacun)}
                icon={IconCard}
                onClick={() => setShowMedian(true)}
                pending={kpiPending}
                {...compareProps}
              />
              <KpiCompareCard
                label="Rashod"
                value={formatNumber(expense)}
                unit="RSD"
                changePct={percentChange(expense, expensePrev)}
                icon={IconCart}
                pending={loadingOverviewExtra && !overviewExtraRaw}
                {...compareProps}
                onClick={() => setShowExpenses(true)}
                invert
              />
            </div>
          </LoadingOverlay>

          {/* Revenue per hour + payments and comparison */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            <Card
              title="Promet po satima"
              icon={IconChart}
              className="xl:col-span-8"
              action={
                <Button variant="ghost" size="sm" onClick={() => setShowRevenueDetail(true)}>
                  Detalji
                </Button>
              }
            >
              <RevenueChart
                startDate={startDate}
                endDate={endDate}
                warehouse={warehouse}
                darkMode={darkMode}
                hourlyRows={current.hourlyRows}
                loading={current.loading}
                error={current.error}
                onRetry={handleRetry}
              />
            </Card>

            <div className="flex flex-col gap-4 xl:col-span-4">
              <Card title="Plaćanja" icon={IconCard} className="flex-1">
                <PaymentsDonut
                  data={payments}
                  darkMode={darkMode}
                  loading={loadingOverviewExtra}
                  error={overviewExtraError}
                  onRetry={handleRetry}
                />
              </Card>

              <Card title="Poređenje prometa">
                <dl className="flex flex-col divide-y divide-slate-100 text-sm dark:divide-slate-800">
                  <div className="flex items-center justify-between py-2 first:pt-0">
                    <dt className="text-slate-500 dark:text-slate-400">Ovaj period</dt>
                    <dd className="tabular-nums font-semibold text-slate-900 dark:text-slate-50">{formatNumber(current.promet)} RSD</dd>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <dt className="text-slate-500 dark:text-slate-400">{compareMode === "year" ? "Prošla godina" : "Prethodni period"}</dt>
                    <dd className="tabular-nums font-semibold text-slate-900 dark:text-slate-50">{formatNumber(previous.promet)} RSD</dd>
                  </div>
                  <div className="flex items-center justify-between py-2 last:pb-0">
                    <dt className="text-slate-500 dark:text-slate-400">Razlika</dt>
                    <dd
                      className={`tabular-nums font-semibold ${
                        revenueDiff >= 0 ? "text-success-dark dark:text-success-light" : "text-danger-dark dark:text-danger-light"
                      }`}
                    >
                      {revenueDiff >= 0 ? "+" : ""}
                      {formatNumber(revenueDiff)} RSD
                    </dd>
                  </div>
                </dl>
              </Card>
            </div>
          </div>

          {/* Items, employees, shift */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            <Top5ItemsChart startDate={startDate} endDate={endDate} warehouse={warehouse} refreshKey={refreshTick} onRetry={handleRetry} onDetails={() => setShowSalesAnalysis(true)} />

            <Card title="Promet i rashod po radnicima" icon={IconUsers}>
              {employeeError && employeeRevenue.length === 0 ? (
                <ErrorState onRetry={handleRetry} />
              ) : employeeRevenue.length === 0 && loadingEmployeeRevenue ? (
                <ListSkeleton rows={5} />
              ) : employeeRevenue.length === 0 ? (
                <EmptyState title="Nema prometa po radnicima" hint="Probaj drugi period ili magacin." />
              ) : (
                <LoadingOverlay loading={loadingEmployeeRevenue}>
                  <EmployeeSplitBars rows={employeeRevenue.slice(0, 8).map((e) => ({ label: e.worker_name, revenue: parseFloat(e.prihod) || 0, expense: parseFloat(e.rashod) || 0 }))} />
                  {employeeRevenue.length > 8 && (
                    <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">i još {employeeRevenue.length - 8}</p>
                  )}
                </LoadingOverlay>
              )}
            </Card>

            <Card title="Trenutna smena" icon={IconClock}>
              {shiftError && !shiftDashboardRaw ? (
                <ErrorState onRetry={handleRetry} />
              ) : (
                <LoadingOverlay loading={loadingShiftDashboard}>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                    <Stat label="Trenutno na stolovima" value={formatNumber(currentlyOnTables)} unit="RSD" />
                    <div className="min-w-0">
                      <dt className="text-xs text-slate-500 dark:text-slate-400">Zauzetost</dt>
                      <dd className="mt-0.5"><OccupancyMeter pct={occupancy} /></dd>
                    </div>
                    <Stat label="Gotovina" value={formatNumber(shiftCash)} unit="RSD" />
                    <Stat label="Kartice" value={formatNumber(shiftCards)} unit="RSD" />
                    <Stat label="Rashod" value={formatNumber(shiftExpense)} unit="RSD" />
                    <Stat label="Aktivni konobari" value={String(activeWaiters.length)} title={activeWaiters.join(", ")} />
                    <Stat
                      className="col-span-2"
                      label="Najbolji sto"
                      value={bestTable || "-"}
                      sub={bestTable ? `${formatNumber(bestTableValue)} RSD` : null}
                    />
                  </dl>
                </LoadingOverlay>
              )}
            </Card>
          </div>

          <RecordListModal
            open={showReceipts}
            onClose={() => setShowReceipts(false)}
            title="Računi u prometu"
            endpoint="/receipts.php"
            query={`od=${startDate}&do=${endDate}${whParam}`}
            listKey="racuni"
            getId={(r) => r.pfr}
            idLabel="PFR broj"
            detail={{ endpoint: "/receipt_items.php", getQuery: (r) => `id=${r.id}`, render: (d) => <ReceiptDetail data={d} /> }}
            noun="računa"
            emptyTitle="Nema računa u izabranom periodu"
          />
          <MedianReceiptModal
            open={showMedian}
            onClose={() => setShowMedian(false)}
            query={`od=${startDate}&do=${endDate}${whParam}`}
            compareQuery={prevStartDate && prevEndDate ? `od=${prevStartDate}&do=${prevEndDate}${whParam}` : null}
            compareLabel={compareMode === "year" ? "prošlu godinu" : "prethodni period"}
            compareRange={prevStartDate && prevEndDate ? formatDateRange(prevStartDate, prevEndDate) : ""}
          />
          <RecordListModal
            open={showExpenses}
            onClose={() => setShowExpenses(false)}
            title="Rashodi"
            endpoint="/expenses.php"
            query={`od=${startDate}&do=${endDate}${whParam}`}
            listKey="rashodi"
            getId={(r) => r.id}
            idLabel="ID rashoda"
            detail={{ local: true, render: (r) => <ExpenseDetail row={r} /> }}
            noun="rashoda"
            emptyTitle="Nema rashoda u izabranom periodu"
          />

          <SalesByDayModal
            open={showRevenueDetail}
            onClose={() => setShowRevenueDetail(false)}
            startDate={startDate}
            endDate={endDate}
            compareStart={prevStartDate}
            compareEnd={prevEndDate}
            compareLabel={compareMode === "year" ? "Prošla godina" : "Prethodni period"}
            warehouse={warehouse}
            darkMode={darkMode}
          />

          <Modal open={showSalesAnalysis} onClose={() => setShowSalesAnalysis(false)} title="Analiza prodaje">
            <div className="flex flex-col gap-4">
              <CategoryChart startDate={startDate} endDate={endDate} warehouse={warehouse} />
              <GroupShareChart startDate={startDate} endDate={endDate} warehouse={warehouse} />
              <TopItemsList startDate={startDate} endDate={endDate} warehouse={warehouse} totalRevenue={current.promet} />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}

// While waiting for settings: a page-shaped placeholder instead of a spinner, so nothing jumps when data arrives.
function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-4" role="status" aria-label="Učitavanje podešavanja">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 dark:border-slate-800 dark:bg-slate-800 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-surface px-4 py-3.5 dark:bg-slate-900">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="mt-3 h-6 w-28" />
            <Skeleton className="mt-3 h-4 w-20" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <Card className="xl:col-span-8">
          <ChartSkeleton height={240} />
        </Card>
        <Card className="xl:col-span-4">
          <ListSkeleton rows={4} />
        </Card>
      </div>
      <span className="sr-only">Učitavanje podešavanja</span>
    </div>
  );
}

function Stat({ label, value, unit, sub, title, className = "" }) {
  return (
    <div title={title} className={`min-w-0 ${className}`}>
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="mt-0.5 flex items-baseline gap-1 tabular-nums">
        <span className="truncate text-base font-semibold text-slate-900 dark:text-slate-50">{value}</span>
        {unit && <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">{unit}</span>}
      </dd>
      {sub && <dd className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{sub}</dd>}
    </div>
  );
}
